import { BattleplanComponentType, } from './IBattleplan.js';
import { fromAoSData } from '../models/IAbility.js';
/**
 * Slugify a condition string into a deterministic ID
 */
function slugify(text) {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}
// Ordinal words used in round phrases ("the first battle round", "the second
// battle round onwards", …).
const ROUND_ORDINALS = {
    first: 1,
    second: 2,
    third: 3,
    fourth: 4,
    fifth: 5,
};
/**
 * Parse round numbers from a string like "1, 3 and 5" or "2 and 4"
 */
function parseRoundNumbers(roundText) {
    const numbers = [];
    const matches = roundText.match(/\d+/g);
    if (matches) {
        for (const m of matches) {
            numbers.push(parseInt(m, 10));
        }
    }
    return numbers.sort((a, b) => a - b);
}
/**
 * Determine if a line is a "for each" counter pattern.
 * Matches "for each X" or "each X ... you control" but NOT "at least"
 * which is a checkbox condition.
 */
function isCounterPattern(text) {
    const lower = text.toLowerCase();
    // "for each" pattern (e.g., "for each Gnarlroot objective that you control")
    if (/for each\b/i.test(lower))
        return true;
    // "each ... you control" without "at least" (e.g., "each secondary objective that you control")
    if (/\beach\b.*\byou control\b/i.test(lower) && !/at least/i.test(lower))
        return true;
    return false;
}
/**
 * Determine the maxCount for counter-type rules based on the condition text
 */
function determineMaxCount(conditionText) {
    const lower = conditionText.toLowerCase();
    if (lower.includes('secondary objective'))
        return 2;
    if (lower.includes('gnarlroot') || lower.includes('oakenbrow'))
        return 2;
    // Default for "for each" patterns with terrain features
    if (lower.includes('terrain feature'))
        return 3;
    return 2;
}
/**
 * Detect resource accumulation patterns (e.g., "liferoot point")
 */
function detectResource(text) {
    const match = text.match(/(\w+)\s+points?\b/i);
    if (match) {
        const candidate = match[1].toLowerCase();
        // Only recognize non-"victory" resource names
        if (candidate !== 'victory') {
            return candidate;
        }
    }
    return undefined;
}
/**
 * Parse scoring text to extract structured scoring rules
 */
export function parseScoringRules(scoringText, battleplanName) {
    const rules = [];
    if (!scoringText || !scoringText.trim())
        return rules;
    // Normalize markdown-style bullets ("* " / "- " at line start) to • so the
    // bullet-based parsing below works regardless of which marker was authored.
    scoringText = scoringText.replace(/(^|\n)[ \t]*[*-][ \t]+/g, '$1• ');
    // Scoring rules in the data are marked with bullet points (•).
    // Non-bullet lines are preambles/context that we scan for modifiers but don't emit as rules.
    // Exception: some non-bullet lines contain scoring rules (e.g., Noxious Nexus end-of-battle bonus).
    // First, scan the full text for preamble modifiers
    let preambleExcludedRounds = [];
    if (/cannot be controlled in the first battle round/i.test(scoringText)) {
        preambleExcludedRounds = [1];
    }
    // Split on bullet points to get the scoring rule lines.
    // Each bullet segment may contain trailing non-bullet text after a newline
    // (e.g., "In addition, ..." appended after the last bullet), so split each
    // segment on newlines and take only the first line as the bullet rule.
    const bulletSegments = scoringText.split('•').slice(1); // first element is before first bullet
    const bulletLines = [];
    const trailingNonBulletLines = [];
    for (const segment of bulletSegments) {
        const subLines = segment
            .split('\n')
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
        if (subLines.length > 0) {
            bulletLines.push(subLines[0]);
            // Any subsequent lines are non-bullet text that was after the bullet
            for (let i = 1; i < subLines.length; i++) {
                trailingNonBulletLines.push(subLines[i]);
            }
        }
    }
    // Also check for non-bullet scoring lines from the full text
    const nonBulletLines = [
        ...trailingNonBulletLines,
        ...scoringText
            .split('\n')
            .map((s) => s.trim())
            .filter((s) => {
            // Only include non-bullet lines that contain actual scoring (not preamble)
            if (s.includes('•'))
                return false;
            if (/^each player scores/i.test(s))
                return false;
            if (/cannot be controlled/i.test(s))
                return false;
            if (!s.length)
                return false;
            // Must have a points pattern
            return /scores?\s+\d+\s+\w+\s+points?/i.test(s);
        }),
    ];
    // Deduplicate non-bullet lines
    const uniqueNonBulletLines = [...new Set(nonBulletLines)];
    const lines = [...bulletLines, ...uniqueNonBulletLines];
    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed)
            continue;
        // Extract points - look for "score(s) X victory points", "X victory point", "X liferoot point(s)"
        const pointsMatch = trimmed.match(/scores?\s+(\d+)\s+(\w+)\s+points?/i);
        if (!pointsMatch)
            continue; // Skip lines that don't have a scoring pattern
        const points = parseInt(pointsMatch[1], 10);
        const resource = detectResource(trimmed);
        // Build the display condition text
        let condition = trimmed;
        // Strip "In addition, " prefix
        condition = condition.replace(/^In addition,\s*/i, '');
        // Parse round restrictions first (before stripping scoring prefix,
        // since round prefix may precede the scoring phrase)
        let rounds;
        // "In battle rounds X, Y and Z, ..."
        const roundPrefixMatch = condition.match(/^In battle rounds?\s+([\d\s,and]+),\s*/i);
        if (roundPrefixMatch) {
            rounds = parseRoundNumbers(roundPrefixMatch[1]);
            // Strip the round prefix from condition text and capitalize
            condition = condition.slice(roundPrefixMatch[0].length);
            condition = condition.charAt(0).toUpperCase() + condition.slice(1);
        }
        // "In the first battle round, ..." -> that single round
        const inOrdinalRoundMatch = condition.match(/^In the (\w+) battle round,\s*/i);
        if (!rounds && inOrdinalRoundMatch) {
            const n = ROUND_ORDINALS[inOrdinalRoundMatch[1].toLowerCase()];
            if (n) {
                rounds = [n];
                condition = condition.slice(inOrdinalRoundMatch[0].length);
                condition = condition.charAt(0).toUpperCase() + condition.slice(1);
            }
        }
        // "From the second battle round onwards, ..." -> that round through 5
        const fromOrdinalRoundMatch = condition.match(/^From the (\w+) battle round onwards?,\s*/i);
        if (!rounds && fromOrdinalRoundMatch) {
            const n = ROUND_ORDINALS[fromOrdinalRoundMatch[1].toLowerCase()];
            if (n) {
                rounds = [1, 2, 3, 4, 5].filter((r) => r >= n);
                condition = condition.slice(fromOrdinalRoundMatch[0].length);
                condition = condition.charAt(0).toUpperCase() + condition.slice(1);
            }
        }
        // Strip "Score X victory points if you " prefix (but keep "for each" intact)
        condition = condition.replace(/^scores?\s+\d+\s+(?:victory\s+)?points?\s+if you\s+/i, '');
        condition = condition.replace(/^scores?\s+\d+\s+(?:victory\s+)?points?\s+(?=for each\b)/i, '');
        // Also handle "a player scores X victory points if they"
        condition = condition.replace(/^a\s+player\s+scores?\s+\d+\s+(?:victory\s+)?points?\s+if\s+they\s+/i, '');
        // Capitalize first letter
        condition = condition.charAt(0).toUpperCase() + condition.slice(1);
        // "(first battle round only)" at the end
        const firstRoundOnlyMatch = condition.match(/\s*\(first battle round only\)\s*\.?$/i);
        if (firstRoundOnlyMatch) {
            rounds = [1];
            condition = condition.slice(0, firstRoundOnlyMatch.index).trim();
            // Ensure it ends with a period if it doesn't
            if (!condition.endsWith('.'))
                condition += '.';
        }
        // "at the end of the battle" -> rounds [5]
        if (/at the end of the battle/i.test(condition)) {
            rounds = [5];
        }
        // Apply preamble round restrictions if no explicit round restriction was found
        if (!rounds &&
            preambleExcludedRounds.length > 0 &&
            !/at the end of the battle/i.test(trimmed)) {
            rounds = [1, 2, 3, 4, 5].filter((r) => !preambleExcludedRounds.includes(r));
        }
        // Lifecycle: primary/secondary objective rules are not active round 1
        // (stated in an ability, not in the scoring text itself)
        if (!rounds &&
            battleplanName?.toLowerCase() === 'lifecycle' &&
            (/\bprimary objective\b/i.test(condition) || /\bsecondary objective\b/i.test(condition))) {
            rounds = [2, 3, 4, 5];
        }
        // Determine control type
        const isCounter = isCounterPattern(condition) || !!resource;
        const controlType = isCounter ? 'counter' : 'checkbox';
        // Determine maxCount for counters
        const maxCount = isCounter ? determineMaxCount(condition) : undefined;
        // Generate deterministic ID from condition text
        const id = slugify(condition);
        const rule = {
            id,
            condition,
            points,
            controlType,
        };
        if (maxCount !== undefined)
            rule.maxCount = maxCount;
        if (rounds !== undefined)
            rule.rounds = rounds;
        if (resource !== undefined)
            rule.resource = resource;
        rules.push(rule);
    }
    return rules;
}
/**
 * Convert raw battleplan component to structured component
 */
function parseComponent(raw, battleplanName) {
    const component = {
        contentType: raw.contentType,
        text: raw.text,
        title: raw.title,
        ability: raw.ability ? fromAoSData(raw.ability) : null,
    };
    if (raw.contentType === 'scoring' && raw.text) {
        component.scoringRules = parseScoringRules(raw.text, battleplanName);
    }
    return component;
}
/**
 * Convert raw container to structured battleplan
 */
function parseContainer(container) {
    const components = container.components.map((c) => parseComponent(c, container.name));
    // Extract abilities from components
    const abilities = components
        .filter((c) => c.ability)
        .map((c) => c.ability)
        .filter(Boolean);
    // Extract twist text
    const twistComponent = components.find((c) => c.contentType === BattleplanComponentType.Twist);
    const twist = twistComponent?.text || undefined;
    // Extract scoring information
    const scoringComponent = components.find((c) => c.contentType === BattleplanComponentType.Scoring);
    const scoringText = scoringComponent?.text || undefined;
    const scoringRules = scoringComponent?.scoringRules || [];
    // Build full rules text by combining all component text
    const rulesText = components
        .map((c) => c.text)
        .filter(Boolean)
        .join('\n\n');
    return {
        name: container.name,
        rulesText,
        components,
        abilities,
        twist,
        scoringText,
        scoringRules,
    };
}
/**
 * Parse a raw battleplan document into structured format
 */
export function parseBattleplanDocument(raw, battlePack) {
    const sections = raw.sections.map((section) => ({
        name: section.name,
        containers: section.containers.map((container) => ({
            name: container.name,
            containerType: container.containerType,
            battleplan: parseContainer(container),
        })),
    }));
    return {
        title: raw.title,
        sections,
        battlePack,
    };
}
/**
 * Build a battleplans map from a battleplan document
 */
export function buildBattleplansMap(document) {
    const battleplansMap = new Map();
    const battlePackMap = new Map();
    // Extract all battleplans from all sections and containers
    for (const section of document.sections) {
        for (const container of section.containers) {
            battlePackMap.set(container.battleplan.name, container.battleplan);
        }
    }
    battleplansMap.set(document.battlePack, battlePackMap);
    return battleplansMap;
}
/**
 * Merge multiple battleplan maps into one
 */
export function mergeBattleplanMaps(...maps) {
    const merged = new Map();
    for (const map of maps) {
        for (const [battlePack, battleplans] of map.entries()) {
            if (!merged.has(battlePack)) {
                merged.set(battlePack, new Map());
            }
            const existingBattleplans = merged.get(battlePack);
            for (const [name, battleplan] of battleplans.entries()) {
                existingBattleplans.set(name, battleplan);
            }
        }
    }
    return merged;
}
